import { AfterViewInit, Component, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { combineLatestWith, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { JsonCatalog, PortalCatalog } from '@src/app/model/portal-catalog';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { Id } from '@ess/jg-rule-executor';
import { Retailer } from '@src/app/model/retailer';
import { SingleFilterSetting } from '@src/app/model/portal-filter-settings';
import { first, map } from 'rxjs/operators';
import { AssetService } from '@src/app/services/asset/asset.service';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PortalModuleCategory } from '@src/app/model/portal-module-category';
import { CatalogInput, SubjectType } from '@src/app/generated/globalTypes';
import { cloneDeep } from 'lodash';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';

@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
  standalone: false,
})
export class CatalogComponent implements OnInit, AfterViewInit {
  public catalog: PortalCatalog;
  public moduleCategories: PortalModuleCategory[];
  public filterSettingsAsArray: MatTableDataSource<SingleFilterSetting>
    = new MatTableDataSource<SingleFilterSetting>([]);

  public displayedColumns: string[]
    = ['moduleCategoryReference', 'attributeId', 'attributeName', 'attributeType', 'attributeValue', 'action'];

  private _componentDestroyed$: Subject<void> = new Subject();
  private _catalogFailMsg: string = 'catalog.update.failure';
  private _filterSettingFailMsg: string = 'catalog.addFilterSetting.failure';
  private _assetFailMsg: string = 'blueprints.uploadAsset.failure';
  private _assetSuccessMsg: string = 'blueprints.uploadAsset.success';
  private _skipFields: string[] = ['id', 'scope', 'moduleCategories'];

  readonly sort = viewChild(MatSort);

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _scopeService: ScopeService,
    private _assetService: AssetService
  ) {
  }

  public ngOnInit(): void {
    this._getCatalog();
  }

  public ngAfterViewInit(): void {
    this.filterSettingsAsArray.sort = this.sort();
  }

  public openEditCatalogJsonDialog($event: any): void {
    const scope = this._scopeService.currentScope$.getValue()?.id;
    const jsonCatalog = new JsonCatalog(this.catalog, this.moduleCategories);
    if (scope) {
      $event.stopPropagation();
      const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
        'catalog',
        jsonCatalog,
        'catalog.update.header',
        'buttons.submit',
        this._skipFields
      ));
      JsonModal.useJsonDialog(
        dialogRef,
        (json => this._updaterWithJsonCatalog(json, scope)).bind(this),
        this._translateService.instant(this._catalogFailMsg),
        this._apolloService.getCatalogRefresh.bind(this._apolloService)
      );
    }
  }

  public openEditCatalogFormDialog($event: any): void {
    const scope = this._scopeService.currentScope$.getValue()?.id;
    const formCatalog = new JsonCatalog(this.catalog, this.moduleCategories);
    if (scope) {
      $event.stopPropagation();
      const dialogRef = TypedFormModal.openTypedFormModalDialog(
        this._dialog, new TypedFormModalDataObject(
          'catalog',
          SubjectType.CatalogType,
          formCatalog,
          formCatalog.id,
          this._skipFields,
          undefined,
          ''
        )
      );
      TypedFormModal.useFormDialog(
        dialogRef,
        ((edited: CatalogInput) => this._updaterWithJsonCatalog(JsonCatalog.toJsonInput(edited), scope)).bind(this),
        this._translateService.instant(this._catalogFailMsg),
        this._apolloService.getCatalogRefresh.bind(this._apolloService)
      );
    }
  }

  public openAddFilterSettingDialog($event: any): void {
    const scope = this._scopeService.currentScope$.getValue()?.id;
    if (scope) {
      $event.stopPropagation();
      const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
        'filterSetting',
        new SingleFilterSetting(
          'MODULE_CATEGORY_ID',
          undefined,
          'ATTRIBUTE_ID',
          undefined,
          undefined,
          'ATTRIBUTE_VALUE'
        ),
        'catalog.addFilterSetting.header',
        'buttons.submit',
        ['asset']
      ));
      JsonModal.useJsonDialog(
        dialogRef,
        (json => this._upsertFilterSetting(json['filterSetting'], scope)).bind(this),
        this._translateService.instant(this._filterSettingFailMsg),
        this._apolloService.getCatalogRefresh.bind(this._apolloService)
      );
    }
  }

  private _upsertFilterSetting(input: JSON | SingleFilterSetting, scope): Observable<boolean> {
    if (input) {
      const fsObj = input instanceof SingleFilterSetting ? input : SingleFilterSetting.fromJson(input);
      fsObj.moduleCategoryReference = this.moduleCategories
        .find(mc => mc.id.toString() === fsObj.moduleCategoryId).reference; // Has to exist, necessary for upsert
      const catalogInput = this.catalog.toGqlInput();
      const newFilterSettings = this.catalog.filterSettings.upsertSimple(
        fsObj.moduleCategoryReference,
        fsObj.attributeId,
        fsObj.attributeValue,
        fsObj.asset
      );
      catalogInput.filterSettings = newFilterSettings.toGqlInput();
      return this._updater({ catalog: catalogInput } as object as JSON, scope);
    } else {
      return throwError(() => 'invalid filter setting object');
    }
  }

  public assetUpload(filterSetting: SingleFilterSetting, event): void {
    const scope = this._scopeService.currentScope$.getValue()?.id;
    if (event.target.files.length > 0 && scope) {
      const assetMap = new Map()
        .set('moduleCategory', filterSetting.moduleCategoryId)
        .set('attribute', filterSetting.attributeId)
        .set('attributeValue', filterSetting.attributeValue);
      this._assetService.uploadAsset$(event.target.files[0], assetMap)
        .pipe(first())
        .subscribe({
          next: (url) => {
            if (!url) {
              window.alert(this._translateService.instant(this._assetFailMsg));
            } else {
              this._apolloService.getCatalogRefresh();
              window.alert(this._translateService.instant(this._assetSuccessMsg));
            }
          },
          error: (_) => {
            window.alert(this._translateService.instant(this._assetFailMsg));
          },
        });
    }
  }

  private _getCatalog(): void {
    this._apolloService.getCatalog$()
      .pipe(
        takeUntil(this._componentDestroyed$),
        combineLatestWith(this._apolloService.getAllModuleCategories$()),
        combineLatestWith(this._apolloService.getAllAttributes$(null, null).pipe(map(att => att?.attributes)))
      )
      .subscribe(([[catalog, categories], attributes]) => {
        this.catalog = catalog;
        this.moduleCategories = categories;
        this.filterSettingsAsArray.data = catalog.filterSettings.asObjectArray(categories, attributes);
      });
  }

  private _updater(catalog: JSON, scope: Id<Retailer>): Observable<boolean> {
    const updatedCatalog = this._filterSettingsKeysToIds(catalog['catalog']);
    return this._apolloService.updateCatalog$({ catalog: updatedCatalog } as object as JSON, [scope]);
  }

  private _updaterWithJsonCatalog(catalog: JSON, scope: Id<Retailer>): Observable<boolean> {
    const catalogInput = JsonCatalog.toCatalogInput(catalog['catalog']);
    return this._updater({ catalog: catalogInput } as object as JSON, scope);
  }

  private _filterSettingsKeysToIds(catalog: CatalogInput): CatalogInput {
    const updatedCatalog = cloneDeep(catalog);
    updatedCatalog.filterSettings.values = updatedCatalog.filterSettings.values.map((fs) => {
      const categoryId = this.moduleCategories.find(mc => mc.reference === fs.key).id;
      return { key: categoryId.toString(), value: fs.value };
    });
    return updatedCatalog;
  }

  public sortChange(sortState: Sort): void {
  }
}
